import { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import duration, { Duration } from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import { milliToCasualDisplay, stringToDefaultDisplay } from '../utils/DateUtils';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(utc);

export interface TimerProps {
  /**
   * Day js Duration object, can be manipulate to different display format
   */
  countDown: Duration;
  /**
   * Indicate the countdown reached the end
   */
  countDownEnded: boolean;
  /**
   * Reset function to the initial millisecond, if provided a new millisecond to this function, will use the new value to reset instead
   */
  reset: (custom?: number) => void;
}

export const useCountDown = (millisecond: number): TimerProps => {
  const [countDown, setCountDown] = useState(dayjs.duration(millisecond));

  useEffect(() => {
    const interval = setInterval(() => {
      setCountDown((left) => {
        if (left.asMilliseconds() > 0) {
          return left.subtract(1000, 'milliseconds');
        }
        return left;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [millisecond]);

  const reset = useCallback(
    (custom?: number) => setCountDown(dayjs.duration(custom || millisecond)),
    [millisecond]
  );

  // will reset if hook prop change, if remain the same will not trigger
  useEffect(() => {
    reset();
  }, [millisecond, reset]);

  return {
    countDown,
    reset,
    countDownEnded: countDown.asMilliseconds() <= 0,
  };
};

export const useDateTimeFormat = (millisecond: any): string | null =>
  useMemo(() => milliToCasualDisplay(millisecond), [millisecond]);

export const useDateTimeStringFormat = (date: string): string | null =>
  useMemo(() => stringToDefaultDisplay(date), [date]);

export const useDateOnly = (millisecond: number): string | null =>
  useMemo(() => milliToCasualDisplay(millisecond, true), [millisecond]);
